import { useFormik } from 'formik';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { useAuthStore, useViewSettingsStore } from '../..';
import useAccountUpdate from '../../hooks/useAccountUpdate';
import useAccountValidationSchema from '../../hooks/useAccountValidationSchema';
import useClosable from '../../hooks/useClosable';
import useLogout from '../../hooks/useLogout';
import { ClosableOrder } from '../../store/ClosableStore';
import ChangePassword from '../ChangePassword';
import commonStyles from '../Common.module.css';
import editStyles from '../Edit.module.css';
import EditControls from '../EditControls';
import StringInput from '../inputs/StringInput';
import Modal from '../Modal';

function Account() {
    const navigate = useNavigate();
    const logout = useLogout();
    const updateAccount = useAccountUpdate();
    const authStore = useAuthStore();
    const viewSettingsStore = useViewSettingsStore();
    const validationSchema = useAccountValidationSchema();
    const [isChangePasswordWindowOpen, setIsChangePasswordWindowOpen] = useState(false);

    const formik = useFormik<IAccountState>({
        initialValues: AccountState.empty(),
        validationSchema: toFormikValidationSchema(validationSchema),
        onSubmit: async (values) => {
            await updateAccount(values);
        },
    });

    const cancel = () => navigate(-1);
    useClosable(ClosableOrder.Medium, cancel);

    const logoutInternal = () => {
        navigate("/");
        logout();
    };

    useEffect(() => {
        if (authStore.user && authStore.userSettings) {
            formik.resetForm({
                values: {
                    firstName: authStore.user!.firstName,
                    lastName: authStore.user!.lastName,
                    companyName: authStore.userSettings!.profileSettings.companyName,
                    email: authStore.user.email,
                },
            });
        }
    }, [authStore.user, authStore.userSettings]);

    if (!authStore.user || !authStore.userSettings)
        return null;

    return (
        <>
            <Modal
                header="User Settings"
                midWidth={!viewSettingsStore.isMobile}
                fullWidth={viewSettingsStore.isMobile}
                onClose={cancel}
            >
                <div className={editStyles.column}>
                    <StringInput formik={formik} label="FIRST NAME" name="firstName" />
                    <StringInput formik={formik} label="LAST NAME" name="lastName" />
                    <StringInput formik={formik} label="EMAIL" name="email" disabled />
                    <StringInput formik={formik} label="COMPANY NAME" name="companyName" disabled={!authStore.user.isIndividual} />
                    <div className={commonStyles.controls}>
                        <div className={commonStyles.controlGroup}>
                            <EditControls
                                isStateChanged={formik.dirty}
                                isUpdateLoading={authStore.isLoading}
                                onUpdate={formik.handleSubmit}
                                onCancel={cancel}
                            />
                        </div>
                        <div className={commonStyles.controlGroup}>
                            <button className={commonStyles.button} onClick={() => setIsChangePasswordWindowOpen(true)}>Change Password</button>
                            <button className={commonStyles.button} onClick={() => viewSettingsStore.setIsMobile(!viewSettingsStore.isMobile)}>Switch to {viewSettingsStore.isMobile ? "Desktop" : "Mobile"} View</button>
                            <button className={commonStyles.button} onClick={logoutInternal}>Logout</button>
                        </div>
                    </div>
                </div>
            </Modal>
            {isChangePasswordWindowOpen && (
                <ChangePassword onClose={() => setIsChangePasswordWindowOpen(false)} />
            )}
        </>
    );
};

export default observer(Account);

export interface IAccountState {
    firstName: string | null;
    lastName: string | null;
    companyName: string | null;
    email: string | null;
}

class AccountState implements IAccountState {
    firstName: string | null = null;
    lastName: string | null = null;
    companyName: string | null = null;
    email: string | null = null;

    static empty() {
        return new AccountState();
    }
}